import { StatusBadge } from '@/components/admin/entretiens/status-badge';
import { NpcNoyauPill } from '@/components/admin/npc/npc-card';
import { NpcFacePill } from '@/components/admin/npc/npc-face-pill';
import { NpcReplaysGrid } from '@/components/admin/npc/npc-replays-grid';
import { NpcReplaysSynthesis } from '@/components/admin/npc/npc-replays-synthesis';
import { type CurrentExpected, faceToExpected } from '@/lib/npc-expected-synthesis';
import {
  eurosLabel,
  fractionLabel,
  replaySeriesLine,
  replayStatusLabel,
  replaysSummary,
} from '@/lib/npc-replays-labels';
import { REPLAY_MODE_LABEL, replayStartedLabel } from '@/lib/replay-mark';
import { sessionStatusTone } from '@/lib/session-labels';
import type { NpcReplayList, NpcReplayRow } from '@matter_extractor/shared';
import { Spinner } from '@pms-digital/design-system';
import { Link } from '@tanstack/react-router';

/**
 * L'onglet « Rejeux » de la fiche d'un NPC (npc.md §10, M3; maquette `Fiche`, planche 2).
 *
 * One row per replay of this NPC, most recent first, with what the SESSION recorded: date, series
 * and pass, experience and side, mode, player, status, steps reached, objectives validated, retained
 * noyau, the verdict against the expected noyau, turns, the cost of the device and - under it - the
 * player's (H15), and the door to the session.
 *
 * Since #216 (npc.md §26.4, lot L) the tab sets the expected noyau of the NPC against what each
 * replay found - N10 forbade it before: a band of synthesis per experience, the grid of the six
 * notes of each replay, and the column « Face à l'attendu » (the verdict in words) after « Noyau ».
 * It is still no measure between experiences or versions: a line compares ONE session to the sheet
 * of its NPC, nothing totals two experiences (A10), and no linter shows.
 *
 * « Regarder » opens the person's screen of a replay (§11), beside « Ouvrir », the session's
 * parcours; the series column is a link to the page of its series (§9), under which stands the pass.
 *
 * `replays` is `undefined` while the read is in flight, `null` when it failed.
 */

export interface NpcReplaysTableProps {
  replays: NpcReplayList | null | undefined;
  /** The current version of the NPC and its expected noyau, for the reminder of the band. */
  current?: CurrentExpected | null;
}

// The table of the design system (#216, npc.md §26.7): headers in 14px Regular `--text-secondary`,
// cells in 14px, rules in `--border-default` - gray-700 and gray-200 are those two tokens.
const HEAD = 'px-3 py-2.5 text-left font-normal text-gray-700 text-small whitespace-nowrap';
const CELL = 'px-3 py-3 align-top text-small';

export function NpcReplaysTable({ replays, current = null }: NpcReplaysTableProps) {
  if (replays === undefined) {
    return (
      <div className="flex justify-center py-12">
        <Spinner label="Chargement des rejeux" />
      </div>
    );
  }
  if (replays === null) {
    return (
      <p data-testid="npc-rejeux-erreur" className="text-gray-700">
        Les rejeux de ce NPC n’ont pas pu être chargés.
      </p>
    );
  }
  const rows = replays.replays;
  if (rows.length === 0) {
    return (
      <p data-testid="npc-rejeux-vide" className="text-gray-700 text-small">
        Aucun rejeu n’a encore joué ce NPC.
      </p>
    );
  }

  return (
    <div data-testid="npc-rejeux" className="space-y-6">
      <div className="flex flex-wrap items-baseline justify-between gap-x-6 gap-y-1">
        <p className="text-gray-700 text-small">
          Du plus récent au plus ancien · toutes expériences
        </p>
        <p data-testid="npc-rejeux-total" className="text-gray-700 text-small">
          {replaysSummary(rows)}
        </p>
      </div>
      <NpcReplaysSynthesis rows={rows} current={current} />
      <NpcReplaysGrid rows={rows} current={current} />
      <div className="overflow-x-auto">
        <table data-testid="npc-rejeux-tableau" className="w-full min-w-[64rem] border-collapse">
          <thead>
            <tr className="border-gray-200 border-b">
              {['Date', 'Série', 'Expérience, côté', 'Mode', 'Joueur', 'Statut'].map((label) => (
                <th key={label} scope="col" className={HEAD}>
                  {label}
                </th>
              ))}
              {['Étapes', 'Objectifs'].map((label) => (
                <th key={label} scope="col" className={`${HEAD} text-right`}>
                  {label}
                </th>
              ))}
              <th scope="col" className={HEAD}>
                Noyau
              </th>
              <th scope="col" className={HEAD}>
                Face à l’attendu
              </th>
              {['Tours', 'Coût'].map((label) => (
                <th key={label} scope="col" className={`${HEAD} text-right`}>
                  {label}
                </th>
              ))}
              <th scope="col" className={HEAD}>
                <span className="sr-only">Session</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <ReplayRow key={row.sessionId} row={row} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ReplayRow({ row }: { row: NpcReplayRow }) {
  return (
    <tr data-testid="npc-rejeu" className="border-gray-200 border-b">
      <td className={`${CELL} whitespace-nowrap`}>{replayStartedLabel(row.startedAt)}</td>
      <td className={CELL} data-testid="npc-rejeu-serie">
        {row.seriesId === null ? null : (
          <Link
            to="/admin/npc/series/$id"
            params={{ id: row.seriesId }}
            data-testid="npc-rejeu-serie-lien"
            className="block font-semibold text-bleu-nuit underline-offset-2 hover:underline"
          >
            {row.seriesLabel ?? 'La série'}
          </Link>
        )}
        {replaySeriesLine(row)}
      </td>
      <td className={CELL}>
        <span className="font-semibold text-bleu-nuit">{row.experienceName}</span>
        {row.experienceVersionNumber === null ? null : (
          <span className="text-caption text-gray-700"> v{row.experienceVersionNumber}</span>
        )}
        <span className="block text-caption text-gray-700">
          {row.side === 'production' ? 'production' : 'développement'}
        </span>
      </td>
      <td className={CELL}>{REPLAY_MODE_LABEL[row.mode]}</td>
      <td className={CELL}>
        {row.player === 'server' ? 'serveur' : 'Claude'}
        <span className="block text-caption text-gray-700">
          {row.player === 'server' ? 'joué par le serveur' : 'déclaré'}
        </span>
      </td>
      <td className={CELL}>
        <StatusBadge
          tone={sessionStatusTone(row.status)}
          size="caption"
          data-testid="npc-rejeu-statut"
        >
          {replayStatusLabel(row)}
        </StatusBadge>
      </td>
      <td className={`${CELL} whitespace-nowrap text-right`}>
        {fractionLabel(row.stepsReached, row.stepsTotal)}
      </td>
      <td className={`${CELL} whitespace-nowrap text-right`}>
        {fractionLabel(row.objectivesValidated, row.objectivesTotal)}
      </td>
      <td className={CELL}>
        {row.noyau === null ? '-' : <NpcNoyauPill expected={row.noyau} size="caption" />}
      </td>
      <td className={CELL}>
        <NpcFacePill face={faceToExpected(row)} testId="npc-rejeu-face" />
      </td>
      <td className={`${CELL} text-right`}>{row.turns}</td>
      <td className={`${CELL} whitespace-nowrap text-right`}>
        {eurosLabel(row.costEur)}
        {row.playerCostEur > 0 ? (
          <span className="block text-caption text-gray-700">
            + {eurosLabel(row.playerCostEur)}
          </span>
        ) : null}
      </td>
      <td className={`${CELL} whitespace-nowrap text-right`}>
        <Link
          to="/admin/npc/regarder/$id"
          params={{ id: row.sessionId }}
          data-testid="npc-rejeu-regarder"
          className="text-bleu-nuit underline underline-offset-2"
        >
          Regarder
        </Link>
        <span aria-hidden="true"> · </span>
        <Link
          to="/admin/sessions/$sessionId"
          params={{ sessionId: row.sessionId }}
          data-testid="npc-rejeu-ouvrir"
          className="text-bleu-nuit underline underline-offset-2"
        >
          Ouvrir
        </Link>
      </td>
    </tr>
  );
}
